import type {
    Author, Category, ContentfulSector, Course, NormalizedContent, NormalizedPersistedSectorsState, Product,
    SearchableTicker, SimplePageContent
} from "@tmi/types/entities";
import { CacheError, openDatabase, runTransaction } from "./cache-database";
import type { StoreName } from "./cache-schema";
import { createStore } from "./cache-store";

export type { CacheStore } from "./cache-store";
export { CacheError, CacheClosedError, CacheOpenError } from "./cache-database";
export type { StoreName } from "./cache-schema";

/**
 * The one readiness handle. Every store method awaits the open internally; call `cache.ready()` explicitly
 * only where you want a timeout around the open (see stores/SectorHistory.ts). Rejects with CacheClosedError
 * once a newer build has upgraded the database (a reload is in flight) — callers must treat that as "stop
 * quietly", see the Wave 2 rules.
 */
export const cache = {
    ready: (): Promise< void > => openDatabase().then( () => undefined ),
    transaction
};

/**
 * `runTransaction` behind the same contract as every store method: a failure is always a CacheError carrying
 * `cause` — a failed request, or a throw from `work` — and a CacheError subclass passes through unchanged (F-M3).
 */
async function transaction< R >(
    stores: readonly StoreName[],
    mode: IDBTransactionMode,
    work: ( tx: IDBTransaction ) => R | Promise< R >
): Promise< R > {
    try {
        return await runTransaction( stores, mode, work );
    } catch ( err: unknown ) {
        if ( err instanceof CacheError ) {
            throw err;
        }
        const detail = ( err as { name?: string; message?: string } | null );
        throw new CacheError( `[ cache ] transaction over [ ${ stores.join( ', ' ) } ] failed: ${ detail?.name ?? 'Error' }${ detail?.message ? ` — ${ detail.message }` : '' }`, { cause: err } );
    }
}

export const articles      = createStore< NormalizedContent >( 'articles' );
export const authors       = createStore< Author >( 'authors' );
export const bonusReports  = createStore< NormalizedContent >( 'reports' );
export const categories    = createStore< Category >( 'categories' );
export const courses       = createStore< Course >( 'courses' );
export const products      = createStore< Product >( 'products' );
export const sectors       = createStore< ContentfulSector >( 'sectors' );
export const sectorHistory = createStore< NormalizedPersistedSectorsState >( 'history' );
export const simplePages   = createStore< SimplePageContent >( 'pages' );
export const syncState     = createStore< unknown >( 'sync_state' );
export const tickers       = createStore< SearchableTicker >( 'tickers' );
export const tradeAlerts   = createStore< NormalizedContent >( 'alerts' );
export const webinars      = createStore< NormalizedContent >( 'webinars' );
